import { updateCartPeriodMutation } from '@/api/carts/cartMutations';
import { cartQuery } from '@/api/carts/cartQueries';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import { Spinner } from '@/components/common/Spinner';
import { Card, CardContent } from '@/components/dashboard/DashboardCard';
import { CartAddItemView } from '@/components/dashboard/my-carts/CartAddItemView';
import { CartItemsList } from '@/components/dashboard/my-carts/CartItemsList';
import { CartSummary } from '@/components/dashboard/my-carts/CartSummarry';
import { MyCartsWrapper } from '@/components/dashboard/my-carts/MyCartsWrapper';
import { CartPeriod } from '@/domain/Cart';
import { routes } from '@/routes/routes';
import { Calendar as CalendarIcon } from '@carbon/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notFound, useNavigate, useParams } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const MyCartItemsPage = () => {
  const navigate = useNavigate();
  const { cartId } = useParams({
    from: '/_dashboard-layout/dashboard/my-carts/$cartId/items',
  });

  const [isAddSupplementOpen, setIsAddSupplementOpen] = useState(false);
  const [isChooseSubscriptionPeriodOpen, setIsChooseSubscriptionPeriodOpen] =
    useState(false);

  const cartPeriodOptions = Object.values(CartPeriod)
    .filter((value) => typeof value === 'number')
    .map((value) => ({
      label: `${value} ${value === CartPeriod.MONTHLY ? 'month' : 'months'}`,
      value: value.toString(),
    }));

  const {
    data: cart,
    isLoading,
    isError,
    isFetching,
  } = useQuery(cartQuery(cartId));

  const updatePeriodMutation = useMutation({
    ...updateCartPeriodMutation(cartId),
    onError: () => {
      setIsChooseSubscriptionPeriodOpen(true);
    },
  });

  const handleChangeSubscriptionPeriod = (value: string) => {
    const period = Number(value) as CartPeriod;

    if (period === cart?.data.period) {
      setIsChooseSubscriptionPeriodOpen(false);
      return;
    }

    updatePeriodMutation.mutate({ period });
    setIsChooseSubscriptionPeriodOpen(false);
  };

  const handleDoneAddingSupplements = () => {
    const itemsCount = cart?.data.items?.length ?? 0;

    if (itemsCount === 0) {
      toast.error('Please add at least one supplement to continue');
      return;
    }

    navigate({
      to: routes.dashboard.myCarts.cart.checkout,
      params: { cartId },
    });
  };

  if (isLoading) {
    return (
      <MyCartsWrapper title="Add supplements">
        <Card>
          <CardContent className="flex min-h-40 items-center justify-center">
            <Spinner className="size-10" />
          </CardContent>
        </Card>
      </MyCartsWrapper>
    );
  }

  if (isError || !cart) {
    throw notFound();
  }

  return (
    <MyCartsWrapper title="Add supplements">
      <Card>
        <CardContent>
          <div className="flex flex-col gap-6.25">
            <CartItemsList cartId={cartId} />

            {isAddSupplementOpen ? (
              <CartAddItemView
                cartId={cartId}
                onClose={() => setIsAddSupplementOpen(false)}
              />
            ) : (
              <Button
                variant="outlinePrimary"
                animate
                className="w-fit hover:[&>svg]:rotate-0"
                onClick={() => setIsAddSupplementOpen(true)}
              >
                <PlusIcon />
                <p>Add supplement</p>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <CartSummary cartId={cartId} />

          {isChooseSubscriptionPeriodOpen ? (
            <div className="flex w-full items-center justify-between gap-3 lg:gap-6">
              <Select
                value={cart.data.period.toString()}
                options={cartPeriodOptions}
                onChange={(value) => handleChangeSubscriptionPeriod(value)}
              />
              <Button
                onClick={() => setIsChooseSubscriptionPeriodOpen(false)}
                className="w-1/2"
              >
                Back
              </Button>
            </div>
          ) : (
            <button
              className="text-primary hover:text-accent active:text-accent flex w-fit cursor-pointer items-center gap-2 font-medium transition-all"
              onClick={() => setIsChooseSubscriptionPeriodOpen(true)}
            >
              <CalendarIcon className="size-6" />
              Choose subscription period
            </button>
          )}

          <Button
            onClick={handleDoneAddingSupplements}
            className="self-center"
            disabled={isFetching}
          >
            Done adding supplements
          </Button>
        </CardContent>
      </Card>
    </MyCartsWrapper>
  );
};
